// src/js/modules/BaseConverter.js
/**
 * BaseConverter - Abstract base class for all converter modules
 */
export class BaseConverter {
  constructor(id, name, icon) {
    this.id = id;
    this.name = name;
    this.icon = icon || 'bi bi-calculator';
    this.units = [];
    this.value = 1;
    this.fromUnit = null;
  }

  /**
   * Initialize the converter
   * @param {HTMLElement} container - The container element to render into
   */
  render(container) {
    // Create converter UI
    const converterElement = document.createElement('div');
    converterElement.className = 'converter';
    converterElement.innerHTML = this.getTemplate();
    container.appendChild(converterElement);

    // Set up event listeners
    this.setupEventListeners(converterElement);
    
    // Initialize with default values
    this.fromUnit = this.units[0];
    
    // Update results after initialization
    setTimeout(() => {
      this.updateResults();
    }, 0);
  }

  /**
   * Get the HTML template for the converter
   * @returns {string} HTML template
   */
  getTemplate() {
    return `
      <div class="card">
        <div class="card-header bg-primary text-white">
          <h2 class="card-title h5 mb-0">
            <i class="${this.icon} me-2"></i>${this.name}
          </h2>
        </div>
        <div class="card-body">
          <div class="row mb-4">
            <div class="col-md-4 mb-3 mb-md-0">
              <label for="valueInput" class="form-label">Value</label>
              <input type="number" class="form-control" id="valueInput" value="1" step="any">
            </div>
            <div class="col-md-4 mb-3 mb-md-0">
              <label for="unitSelect" class="form-label">From Unit</label>
              <select class="form-select" id="unitSelect">
                ${this.getUnitOptions()}
              </select>
            </div>
            <div class="col-md-4">
              <label for="toUnitSelect" class="form-label">To Unit (Optional)</label>
              <select class="form-select" id="toUnitSelect">
                <option value="">All Units</option>
                ${this.getUnitOptions()}
              </select>
            </div>
          </div>
          <div class="results mt-4">
            <h3 class="h5 mb-3">Results</h3>
            <div class="row" id="resultsContainer"></div>
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Get HTML options for unit select
   * @returns {string} HTML options
   */
  getUnitOptions() {
    return this.units.map(unit => 
      `<option value="${unit.id}">${unit.name}</option>`
    ).join('');
  }

  /**
   * Set up event listeners for the converter
   * @param {HTMLElement} element - The converter element
   */
  setupEventListeners(element) {
    const valueInput = element.querySelector('#valueInput');
    const unitSelect = element.querySelector('#unitSelect');
    const toUnitSelect = element.querySelector('#toUnitSelect');
  
    // Handle value changes
    valueInput.addEventListener('input', (e) => {
      const inputValue = parseFloat(e.target.value) || 0;
      const hasValidInput = !isNaN(inputValue) && inputValue !== 0;
      
      // Track conversion attempt
      if (hasValidInput && this.fromUnit) {
        try {
          import('../analytics.js').then(({ trackConversionAttempt, trackConversionStart }) => {
            trackConversionAttempt(this.name, hasValidInput, inputValue, this.fromUnit?.name, this.toUnit?.name);
            // Track conversion start for abandonment tracking
            trackConversionStart(this.name, inputValue, this.fromUnit?.name, this.toUnit?.name);
          }).catch(() => {
            // Fallback: dispatch event directly
            window.dispatchEvent(new CustomEvent('conversion_attempted', {
              detail: {
                converterName: this.name,
                hasValidInput: hasValidInput,
                inputValue: inputValue,
                fromUnit: this.fromUnit?.name,
                toUnit: this.toUnit?.name
              }
            }));
            window.dispatchEvent(new CustomEvent('conversion_started', {
              detail: {
                converterName: this.name,
                inputValue: inputValue,
                fromUnit: this.fromUnit?.name,
                toUnit: this.toUnit?.name
              }
            }));
          });
        } catch (error) {
          // Silent fail for analytics
        }
      }
      
      this.value = inputValue;
      this.updateResults();
    });
  
    // Handle from unit changes
    unitSelect.addEventListener('change', (e) => {
      const unitId = e.target.value;
      const oldFromUnit = this.fromUnit;
      this.fromUnit = this.units.find(unit => unit.id === unitId);
      
      // Track unit switch
      if (oldFromUnit && this.fromUnit && oldFromUnit.id !== this.fromUnit.id) {
        try {
          import('../analytics.js').then(({ trackUnitSwitch }) => {
            trackUnitSwitch(this.name, 'from', oldFromUnit.name, this.fromUnit.name);
          }).catch(() => {
            // Fallback: dispatch event directly
            window.dispatchEvent(new CustomEvent('unit_switched', {
              detail: {
                converterName: this.name,
                unitType: 'from',
                fromUnit: oldFromUnit.name,
                toUnit: this.fromUnit.name
              }
            }));
          });
        } catch (error) {
          // Silent fail for analytics
        }
      }
      
      // Reset toUnit if it's the same as fromUnit
      if (this.toUnit && this.toUnit.id === this.fromUnit.id) {
        toUnitSelect.value = '';
        this.toUnit = null;
      }
      
      this.updateResults();
    });
    
    // Handle to unit changes
    toUnitSelect.addEventListener('change', (e) => {
      const unitId = e.target.value;
      const oldToUnit = this.toUnit;
      this.toUnit = unitId ? this.units.find(unit => unit.id === unitId) : null;
      
      // Track unit switch
      if (oldToUnit !== this.toUnit) {
        try {
          import('../analytics.js').then(({ trackUnitSwitch }) => {
            trackUnitSwitch(this.name, 'to', oldToUnit?.name || 'none', this.toUnit?.name || 'all');
          }).catch(() => {
            // Fallback: dispatch event directly
            window.dispatchEvent(new CustomEvent('unit_switched', {
              detail: {
                converterName: this.name,
                unitType: 'to',
                fromUnit: oldToUnit?.name || 'none',
                toUnit: this.toUnit?.name || 'all'
              }
            }));
          });
        } catch (error) {
          // Silent fail for analytics
        }
      }
      
      this.updateResults();
    });
  }

  /**
   * Update conversion results
   */
  updateResults() {
    const element = document.getElementById('resultsContainer');
    if (!element || !this.fromUnit) return;
  
    element.innerHTML = '';
    
    // If toUnit is selected, only show that conversion
    if (this.toUnit) {
      if (this.toUnit.id !== this.fromUnit.id) {
        const result = this.convert(this.value, this.fromUnit.id, this.toUnit.id);
        
        // Track successful conversion
        if (this.value && !isNaN(result)) {
          try {
            import('../analytics.js').then(({ trackConversionCompleted }) => {
              trackConversionCompleted(this.name, this.value, result, this.fromUnit?.name, this.toUnit?.name);
            }).catch(() => {
              // Fallback: dispatch event directly
              window.dispatchEvent(new CustomEvent('conversion_completed', {
                detail: {
                  converterName: this.name,
                  inputValue: this.value,
                  outputValue: result,
                  fromUnit: this.fromUnit?.name,
                  toUnit: this.toUnit?.name
                }
              }));
            });
          } catch (error) {
            // Silent fail for analytics
          }
        }
        
        const resultElement = document.createElement('div');
        resultElement.className = 'col-12 mb-3';
        resultElement.innerHTML = `
          <div class="card result-card h-100">
            <div class="card-body">
              <h4 class="result-value">${this.formatResult(result)}</h4>
              <p class="result-unit mb-0">${this.toUnit.name}</p>
            </div>
          </div>
        `;
        
        element.appendChild(resultElement);
      }
    } else {
      // Show all conversions
      this.units.forEach(toUnit => {
        if (toUnit.id === this.fromUnit.id) return;
        
        const result = this.convert(this.value, this.fromUnit.id, toUnit.id);
        
        // Track successful conversion (only for first result to avoid spam)
        if (this.value && !isNaN(result) && this.units.indexOf(toUnit) === 0) {
          try {
            import('../analytics.js').then(({ trackConversionCompleted }) => {
              trackConversionCompleted(this.name, this.value, result, this.fromUnit?.name, toUnit.name);
            }).catch(() => {
              // Fallback: dispatch event directly
              window.dispatchEvent(new CustomEvent('conversion_completed', {
                detail: {
                  converterName: this.name,
                  inputValue: this.value,
                  outputValue: result,
                  fromUnit: this.fromUnit?.name,
                  toUnit: toUnit.name
                }
              }));
            });
          } catch (error) {
            // Silent fail for analytics
          }
        }
        
        const resultElement = document.createElement('div');
        resultElement.className = 'col-md-6 col-lg-4 mb-3';
        resultElement.innerHTML = `
          <div class="card result-card h-100">
            <div class="card-body">
              <h4 class="result-value">${this.formatResult(result)}</h4>
              <p class="result-unit mb-0">${toUnit.name}</p>
            </div>
          </div>
        `;
        
        element.appendChild(resultElement);
      });
    }
  }

  /**
   * Convert a value from one unit to another
   * @param {number} value - The value to convert
   * @param {string} fromUnitId - The ID of the unit to convert from
   * @param {string} toUnitId - The ID of the unit to convert to
   * @returns {number} The converted value
   */
  convert(value, fromUnitId, toUnitId) {
    // To be implemented by subclasses
    throw new Error('Method not implemented');
  }

  /**
   * Format a result for display
   * @param {number} value - The value to format
   * @returns {string} The formatted value
   */
  formatResult(value) {
    // Format the result with appropriate precision
    if (Math.abs(value) >= 1000) {
      return value.toLocaleString('en-US', { maximumFractionDigits: 2 });
    } else if (Math.abs(value) >= 10) {
      return value.toLocaleString('en-US', { maximumFractionDigits: 4 });
    } else if (Math.abs(value) >= 0.01) {
      return value.toLocaleString('en-US', { maximumFractionDigits: 6 });
    } else {
      return value.toLocaleString('en-US', { maximumSignificantDigits: 6 });
    }
  }
}
